import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { toSentenceCase } from '@noths/polaris-client-utils';

import {
  type TaxonomyNavLink,
  adaptTaxonomyContextToNavLinks,
} from 'src/adapters/storeToUI/adaptTaxonomyContextToNavLinks';
import { adaptTaxonomyContextToParentLink } from 'src/adapters/storeToUI/adaptTaxonomyContextToParentLink';
import { HorizontalScroller } from 'src/components/molecules/HorizontalScroller/HorizontalScroller';
import { BackToParentLevelLink } from 'src/components/molecules/TaxonomyNav/BackToParentLevelLink';
import {
  type TaxonomyNavItem,
  TaxonomyNav,
} from 'src/components/molecules/TaxonomyNav/TaxonomyNav';
import * as styles from 'src/components/molecules/TaxonomyNav/TaxonomyNavContainer.styles';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectIsHrefFollowable } from 'src/redux/content/selectors/selectIsHrefFollowable';
import { selectTaxonomyContext } from 'src/redux/productsQuery/selectors/selectTaxonomyContext';

// The adapters are casing-agnostic, so the displayed label is sentence-cased
// here, at the point of render.
const toNavItem = (link: TaxonomyNavLink): TaxonomyNavItem => ({
  active: link.active,
  crawlable: link.crawlable,
  href: link.href,
  text: toSentenceCase(link.text),
  value: link.value,
});

export const TaxonomyNavContainer = () => {
  const context = useSelector((state: ReduxApplicationState) => selectTaxonomyContext(state));
  const isFollowable = useSelector(selectIsHrefFollowable);
  const items = adaptTaxonomyContextToNavLinks(context, isFollowable);
  const parentLink = adaptTaxonomyContextToParentLink(context);

  // The nav options come from facet counts, which are only refreshed once a
  // listings-api response lands. While a request is in flight the counts can be
  // stale (e.g. mid-navigation the selection has changed but the response has
  // not arrived), so hold the last confirmed nav rather than flash options the
  // response will remove. The URL sync and the request's pending dispatch are
  // batched together, so `pageLoading` is set in the same render as the facet
  // change and this flag never lags behind it.
  const isLoading = useSelector(
    (state: ReduxApplicationState) => state.products.pageLoading !== null,
  );
  const lastConfirmedItems = useRef(items);
  const lastConfirmedParentLink = useRef(parentLink);

  if (!isLoading) {
    lastConfirmedItems.current = items;
    lastConfirmedParentLink.current = parentLink;
  }

  const itemsToRender = isLoading ? lastConfirmedItems.current : items;
  const parentLinkToRender = isLoading ? lastConfirmedParentLink.current : parentLink;

  if (!itemsToRender || itemsToRender.length === 0) {
    return null;
  }

  // A stable signature of the rendered level: its option values plus which one is
  // active. It changes only when the nav actually swaps to a different level or
  // selection, so the scroller brings the active item into view then rather than
  // lingering in a stale scroll offset, while leaving manual scrolling untouched.
  const scrollResetKey = itemsToRender
    .map((item) => `${item.value}:${item.active ? 1 : 0}`)
    .join('|');

  return (
    <div css={styles.row}>
      {parentLinkToRender && (
        <BackToParentLevelLink
          href={parentLinkToRender.href}
          text={toSentenceCase(parentLinkToRender.text)}
        />
      )}
      <HorizontalScroller ariaLabel="Product category navigation" scrollResetKey={scrollResetKey}>
        <TaxonomyNav items={itemsToRender.map(toNavItem)} />
      </HorizontalScroller>
    </div>
  );
};
